<script setup>
/* global axios */
// Controle FLUTUANTE compacto da ligação AstraCall (mute/encerrar/timer). A ligação
// em si fica REGISTRADA na timeline como mensagem voice_call nativa (criada/atualizada
// pelo backend) — este widget é só o controle ao vivo. Áudio 2-vias via WebRTC.
import { ref, reactive, computed, watch, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAstraCall } from 'dashboard/composables/useAstraCall';
import { useAstraCalls } from 'dashboard/composables/useAstraCalls';
import { openWsAudio } from 'dashboard/composables/astracall/wsAudio';
import { ASTRACALL_START } from './AstraCallButton.vue';

const { t } = useI18n();
const astra = useAstraCall();
// lista de chamadas do softphone novo (CallDock) — usada p/ (1) ignorar o popup
// de "incoming" quando a chamada já é nossa de saída e (2) calar o toque quando o
// dock assume a ligação. Ver o watch mais abaixo.
const { callList: chamadasDoDock, isMinhaSaida } = useAstraCalls();
const store = useStore();
const router = useRouter();
const route = useRoute();

// ---- estado da ligação (declarado antes de quem usa) ----
const visible = ref(false);
const ctx = reactive({
  accountId: null,
  instanceName: '',
  conversationId: null,
  phone: '',
  name: '',
  avatar: '',
  callId: null,
  // ligação entregue pela IA: a ponte já aceitou, então o atendente NÃO reaceita
  handoff: false,
});
// idle | mic | calling | in_call | incoming
const phase = ref('mic');
const muted = ref(false);
const seconds = ref(0);

let pc = null;
let micStream = null;
let remoteAudioEl = null;
let wsAudio = null;
let timerId = null;
let statusId = null;

// atalho: clicar no flutuante leva para a conversa da ligação
const goToConversation = () => {
  if (!ctx.conversationId) return;
  router.push(
    `/app/accounts/${ctx.accountId}/conversations/${ctx.conversationId}`
  );
};

// o flutuante só aparece quando o agente NÃO está vendo a conversa da ligação.
// Baseado na ROTA atual (não em getSelectedChat, que fica preso ao sair da conversa
// p/ outra tela → o flutuante nunca reaparecia fora do ticket).
const onCallConversation = computed(() => {
  if (!ctx.conversationId) return false;
  // o param da rota é conversation_id na maioria das telas (conversationId na de team)
  const current = route.params?.conversation_id || route.params?.conversationId;
  return String(current || '') === String(ctx.conversationId);
});
// o flutuante NUNCA aparece quando o agente está na conversa da ligação — nesse
// caso o card na timeline já traz os controles (entrar/atender, mudo, encerrar).
// Fora da conversa (ou sem conversa), o flutuante aparece para tocar/atender.
const showFloating = computed(() => visible.value && !onCallConversation.value);

const accountId = computed(() => store.getters.getCurrentAccountId);
const isIncoming = computed(() => phase.value === 'incoming');

const base = () =>
  `/api/v1/accounts/${ctx.accountId}/kanban/connections/${ctx.instanceName}`;

const statusLabel = computed(() => {
  if (phase.value === 'incoming') return t('CONNECTIONS.call.incoming');
  if (phase.value === 'in_call') return t('CONNECTIONS.call.inCall');
  return t('CONNECTIONS.call.ringing');
});
const duration = computed(() => {
  const m = Math.floor(seconds.value / 60)
    .toString()
    .padStart(2, '0');
  const s = (seconds.value % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
});

// registra o status da ligação na timeline (mensagem voice_call nativa)
const logStatus = (status, durationSeconds = null) => {
  if (!ctx.callId) return;
  axios
    .patch(`${base()}/call/${ctx.callId}/log`, {
      status,
      duration_seconds: durationSeconds,
      conversation_id: ctx.conversationId,
    })
    .catch(() => {});
};

// ---- toque de chamada (definido antes de quem usa) ----
// clearInterval nunca acha o id e o toque vaza entre ligações e não para ao atender).
let ringTimer = null;
let ringCtx = null;
const seenIncoming = new Set();
// esta chamada já foi reconhecida pelo CallDock? (ver o watch mais abaixo)
let vistaNoDock = false;

const stopRing = () => {
  if (ringTimer) clearInterval(ringTimer);
  ringTimer = null;
  try {
    ringCtx?.close();
  } catch (e) {
    /* noop */
  }
  ringCtx = null;
};

const playRing = () => {
  stopRing(); // garante que não sobra toque anterior tocando
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    ringCtx = new AC();
    const beep = () => {
      if (!ringCtx || phase.value !== 'incoming') return;
      const osc = ringCtx.createOscillator();
      const g = ringCtx.createGain();
      osc.frequency.value = 480;
      g.gain.value = 0.08;
      osc.connect(g);
      g.connect(ringCtx.destination);
      osc.start();
      osc.stop(ringCtx.currentTime + 0.4); // oscilador se auto-encerra
    };
    beep();
    ringTimer = setInterval(beep, 1500);
  } catch (e) {
    /* sem som */
  }
};

const cleanup = () => {
  astra.clear();
  stopRing();
  if (timerId) clearInterval(timerId);
  if (statusId) clearInterval(statusId);
  timerId = null;
  statusId = null;
  try {
    wsAudio?.close();
  } catch (e) {
    /* noop */
  }
  try {
    micStream?.getTracks().forEach(tk => tk.stop());
  } catch (e) {
    /* noop */
  }
  try {
    pc?.close();
  } catch (e) {
    /* noop */
  }
  if (remoteAudioEl) {
    remoteAudioEl.srcObject = null;
    remoteAudioEl.remove();
    remoteAudioEl = null;
  }
  pc = null;
  wsAudio = null;
  micStream = null;
};

const fail = msg => {
  logStatus('failed');
  cleanup();
  useAlert(msg);
  visible.value = false;
};

const waitIce = peer =>
  new Promise(resolve => {
    if (peer.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    const check = () => {
      if (peer.iceGatheringState === 'complete') {
        peer.removeEventListener('icegatheringstatechange', check);
        resolve();
      }
    };
    peer.addEventListener('icegatheringstatechange', check);
    // Sem STUN/TURN (o call server é ice-lite) só há candidatos de host, reunidos de
    // imediato. O teto de 2,5s era tempo morto na cara do atendente quando o evento
    // de "complete" não vinha; 600ms cobre o caso normal com folga.
    setTimeout(resolve, 600);
  });

const startTimer = () => {
  seconds.value = 0;
  astra.setSeconds(0);
  timerId = setInterval(() => {
    seconds.value += 1;
    astra.setSeconds(seconds.value); // publica p/ a bolha da timeline mostrar o tempo
  }, 1000);
};

const endCall = async (sendHangup = true) => {
  const dur = seconds.value;
  if (sendHangup && ctx.callId) {
    try {
      await axios.delete(`${base()}/call/${ctx.callId}`);
    } catch (e) {
      /* noop */
    }
  }
  logStatus('completed', dur);
  cleanup();
  visible.value = false;
};

const toggleMute = () => {
  muted.value = !muted.value;
  astra.setMuted(muted.value);
  micStream?.getAudioTracks().forEach(tk => {
    tk.enabled = !muted.value;
  });
};

const pollStatus = () => {
  statusId = setInterval(async () => {
    try {
      const { data } = await axios.get(`${base()}/call/${ctx.callId}`);
      if (data.active === 0) endCall(false);
    } catch (e) {
      /* mantém */
    }
  }, 3000);
};

// Ponte de ÁUDIO por WebSocket (PCM16). O call server pode não fechar WebRTC (UDP);
// aí a chamada ficava muda. A URL wss (com sid + apiKey) vem do backend (call_transport).
const runWs = async wsUrl => {
  wsAudio = await openWsAudio({ wsUrl, micStream });
  phase.value = 'in_call';
  stopRing();
  astra.setActive(ctx.callId, 'in_call');
  logStatus('in_progress');
  startTimer();
  pollStatus();
};

const runWebrtc = async () => {
  // pergunta ao backend o transporte de áudio (o call server pode pedir websocket).
  // Guardamos a ws_url mesmo no modo webrtc para o fallback quando o ICE não fechar.
  let transport = 'webrtc';
  let wsUrl = null;
  try {
    const { data } = await axios.get(`${base()}/call/${ctx.callId}/transport`);
    transport = data?.transport === 'websocket' ? 'websocket' : 'webrtc';
    wsUrl = data?.ws_url || null;
  } catch (e) {
    /* sem info → segue WebRTC */
  }
  if (transport === 'websocket' && wsUrl) {
    await runWs(wsUrl);
    return;
  }

  pc = new RTCPeerConnection({ iceServers: [] });

  // Fallback: se o ICE NUNCA conectar (rede sem UDP), cai pro áudio por WebSocket em
  // vez de ficar mudo. Dispara no 'failed' ou se não conectar em 7s; uma vez só.
  let iceUp = false;
  let fellBack = false;
  const fallbackToWs = () => {
    if (fellBack || iceUp || !wsUrl || !pc) return;
    fellBack = true;
    try {
      pc.close();
    } catch (e) {
      /* noop */
    }
    pc = null;
    if (remoteAudioEl) {
      remoteAudioEl.srcObject = null;
      remoteAudioEl.remove();
      remoteAudioEl = null;
    }
    runWs(wsUrl).catch(() => fail(t('CONNECTIONS.call.failed')));
  };
  pc.oniceconnectionstatechange = () => {
    if (!pc) return;
    const st = pc.iceConnectionState;
    if (st === 'connected' || st === 'completed') iceUp = true;
    else if (st === 'failed') fallbackToWs();
  };

  // `addTrack` já cria o transceiver `sendrecv` (envia E recebe). O
  // `addTransceiver('audio', {direction:'recvonly'})` que havia aqui punha uma
  // SEGUNDA linha `m=audio` na offer — mesmo caso do useAstraCalls.
  micStream.getAudioTracks().forEach(tk => pc.addTrack(tk, micStream));
  pc.ontrack = e => {
    // Havia um `if (!stream) return` aqui: quando o answer não traz `a=msid`,
    // `e.streams` vem vazio e a função desistia SEM criar o elemento de áudio —
    // o RTP do cliente chegava e ninguém tocava. Monta o stream na mão.
    const stream = e.streams?.[0] || new MediaStream([e.track]);
    remoteAudioEl = document.createElement('audio');
    remoteAudioEl.autoplay = true;
    remoteAudioEl.srcObject = stream;
    document.body.appendChild(remoteAudioEl);
    remoteAudioEl.play?.().catch(() => {
      /* autoplay recusado; o elemento segue montado */
    });
  };
  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  await waitIce(pc);
  const { data } = await axios.post(`${base()}/call/${ctx.callId}/webrtc`, {
    sdp_offer: pc.localDescription.sdp,
  });
  if (!data.sdp_answer) throw new Error('no answer');
  await pc.setRemoteDescription({ type: 'answer', sdp: data.sdp_answer });
  // se em 7s o ICE não subiu, assume rede sem UDP e cai pro áudio por WebSocket
  setTimeout(fallbackToWs, 7000);
  phase.value = 'in_call';
  stopRing(); // chamada ativa: garante que o toque parou (qualquer caminho)
  astra.setActive(ctx.callId, 'in_call');
  logStatus('in_progress');
  startTimer();
  pollStatus();
};

const start = async payload => {
  cleanup();
  Object.assign(ctx, payload);
  ctx.callId = null;
  phase.value = 'mic';
  muted.value = false;
  seconds.value = 0;
  visible.value = true;

  try {
    micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (e) {
    cleanup();
    useAlert(t('CONNECTIONS.call.micError'));
    visible.value = false;
    return;
  }
  phase.value = 'calling';
  try {
    const { data } = await axios.post(`${base()}/call`, {
      phone: ctx.phone,
      conversation_id: ctx.conversationId,
    });
    ctx.callId = data.data?.call_id;
    if (!ctx.callId) throw new Error('no call id');
    astra.setActive(ctx.callId, 'calling');
    astra.registerHandlers({ toggleMute, hangup: () => endCall(true) });
  } catch (e) {
    cleanup();
    useAlert(e?.response?.data?.error || t('CONNECTIONS.call.failed'));
    visible.value = false;
    return;
  }
  try {
    await runWebrtc();
  } catch (e) {
    fail(t('CONNECTIONS.call.failed'));
  }
};

// ===== ENTRADA (cliente liga) =====
// ringTimer = id do setInterval (SEPARADO do nó oscilador — não misturar, senão o
// ligação de entrada chega em TEMPO REAL via ActionCable (backend listener SSE →
// broadcast `kanban.astracall.incoming`). O Call/mensagem já foram criados no backend.
const acceptIncoming = async () => {
  stopRing();
  try {
    micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (e) {
    useAlert(t('CONNECTIONS.call.micError'));
    return;
  }
  phase.value = 'calling'; // some o "Atender" na hora do clique, em vez de só no fim
  try {
    // entregue pela IA: a ponte já aceitou a chamada no call server; um segundo
    // accept é recusado e derrubava o atendimento aqui
    if (!ctx.handoff) await axios.post(`${base()}/call/${ctx.callId}/accept`);
    await runWebrtc();
  } catch (e) {
    // sem isto o widget só dizia "falhou": não dava para saber se foi o accept, a
    // troca de SDP ou o microfone
    // eslint-disable-next-line no-console
    console.error('[AstraCall widget] falha ao atender', {
      callId: ctx.callId,
      instancia: ctx.instanceName,
      transferida: ctx.handoff,
      status: e?.response?.status,
      erro: e?.response?.data?.error || e?.message || e,
    });
    fail(t('CONNECTIONS.call.failed'));
  }
};

const rejectIncoming = async () => {
  stopRing();
  try {
    await axios.post(`${base()}/call/${ctx.callId}/reject`);
  } catch (e) {
    /* noop */
  }
  cleanup();
  visible.value = false;
};

const openIncoming = payload => {
  if (visible.value) return; // já em ligação/tocando
  if (seenIncoming.has(payload.call_id)) return;
  seenIncoming.add(payload.call_id);
  // Se ESTE atendente já iniciou a chamada pelo softphone novo (CallDock), o
  // broadcast `incoming` chega de volta pra ele mesmo. Abrir o popup "Atender/
  // Entrar na chamada" aqui (e na bolha voice_call) é redundante: ele já está na
  // ligação pelo dock. `isMinhaSaida` cobre a CORRIDA (marca a conversa como saída
  // no clique, antes de a chamada aparecer no dock) e o callId já reconhecido.
  if (isMinhaSaida({ callId: payload.call_id, conversationId: payload.conversation_id }))
    return;
  Object.assign(ctx, {
    accountId: accountId.value,
    instanceName: payload.instance_name,
    callId: payload.call_id,
    conversationId: payload.conversation_id,
    name: payload.contact_name,
    phone: payload.peer,
    avatar: '',
    handoff: !!payload.handoff,
  });
  phase.value = 'incoming';
  muted.value = false;
  visible.value = true;
  vistaNoDock = false; // cada chamada precisa ser reconhecida pelo dock de novo
  // publica p/ a bolha da timeline mostrar Atender/Recusar e esconder o join nativo
  astra.setActive(payload.call_id, 'incoming');
  astra.registerHandlers({
    accept: acceptIncoming,
    reject: rejectIncoming,
    toggleMute,
    hangup: () => endCall(true),
  });
  playRing();
};

// O toque daqui só parava pelo Atender/Recusar DESTE widget. Quem conduz a ligação
// hoje é o CallDock (softphone novo, que fala direto com o call server por SSE): ao
// atender por lá, este widget continuava na fase `incoming` e seguia bipando — mesmo
// depois de a chamada ser atendida ou encerrada. Aqui ele passa a acompanhar o estado
// de verdade da chamada e se calar junto (chamadasDoDock declarado no topo).
watch(
  () => {
    if (!ctx.callId) return null;
    const c = chamadasDoDock.value.find(
      x => String(x.callId) === String(ctx.callId)
    );
    return c ? c.status || 'desconhecido' : 'sumiu';
  },
  situacao => {
    if (phase.value !== 'incoming') return;
    // rede de segurança: se virou uma chamada MINHA de saída no dock, fecha o popup
    // (o watch dispara quando o dock assume a chamada, mesmo sem estado 'ringing').
    if (isMinhaSaida({ callId: ctx.callId, conversationId: ctx.conversationId })) {
      stopRing();
      cleanup();
      visible.value = false;
      return;
    }
    if (!situacao) return;
    // TRANSFERÊNCIA: a chamada já está EM ANDAMENTO (a ponte atendeu e está segurando
    // a linha), então ela nunca aparece como "ringing" no dock. Esta regra fechava o
    // widget sozinho um instante depois de abrir — o atendente via o card surgir e
    // sumir, e o botão parecia falhar. Aqui só a chamada REALMENTE encerrada fecha.
    if (ctx.handoff) {
      if (situacao === 'sumiu') {
        stopRing();
        cleanup();
        visible.value = false;
      }
      return;
    }
    if (situacao === 'ringing') {
      vistaNoDock = true; // o dock conhece esta chamada — podemos confiar nele
      return;
    }
    // sem SSE (dock desligado/sem permissão) a lista vive vazia: não encerrar por isso
    if (!vistaNoDock) return;

    stopRing();
    cleanup();
    visible.value = false;
  }
);

const onStart = payload => {
  nextTick(() => start(payload));
};
const onIncoming = payload => {
  nextTick(() => openIncoming(payload));
};
emitter.on(ASTRACALL_START, onStart);
emitter.on('kanban:astracall:incoming', onIncoming);
onBeforeUnmount(() => {
  emitter.off(ASTRACALL_START, onStart);
  emitter.off('kanban:astracall:incoming', onIncoming);
  stopRing();
  cleanup();
});
</script>

<template>
  <transition name="astracall-pop">
    <div
      v-if="showFloating"
      class="fixed z-50 flex items-center gap-3 px-3 py-2.5 rounded-2xl shadow-xl bottom-6 ltr:right-6 rtl:left-6 w-72 bg-n-solid-2 outline outline-1 outline-n-weak"
    >
      <button
        class="flex items-center flex-1 min-w-0 gap-3 text-left"
        :title="t('CONNECTIONS.call.goToConversation')"
        @click="goToConversation"
      >
        <div class="relative shrink-0">
          <span
            class="absolute inset-0 rounded-full bg-n-teal-9/30 animate-ping"
            :class="phase === 'in_call' ? 'hidden' : ''"
          />
          <Avatar :src="ctx.avatar" :name="ctx.name || ctx.phone" :size="36" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-1.5">
            <Icon
              icon="i-ri-whatsapp-fill"
              class="size-3.5 text-n-teal-9 shrink-0"
            />
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ ctx.name || ctx.phone }}
            </span>
          </div>
          <div class="text-xs text-n-slate-10 tabular-nums">
            {{ phase === 'in_call' ? duration : statusLabel }}
          </div>
        </div>
      </button>
      <!-- ligação recebida: atender / recusar -->
      <div v-if="isIncoming" class="flex items-center gap-1.5 shrink-0">
        <NextButton
          v-tooltip.top="t('CONNECTIONS.call.accept')"
          icon="i-ph-phone-bold"
          teal
          xs
          class="!rounded-full"
          @click="acceptIncoming"
        />
        <NextButton
          v-tooltip.top="t('CONNECTIONS.call.reject')"
          icon="i-ph-phone-bold"
          ruby
          xs
          class="!rounded-full rotate-[135deg]"
          @click="rejectIncoming"
        />
      </div>
      <!-- ligação em andamento: mudo / encerrar -->
      <div v-else class="flex items-center gap-1.5 shrink-0">
        <NextButton
          v-if="phase === 'in_call'"
          v-tooltip.top="
            muted ? t('CONNECTIONS.call.unmute') : t('CONNECTIONS.call.mute')
          "
          :icon="muted ? 'i-ph-microphone-slash-bold' : 'i-ph-microphone-bold'"
          :color="muted ? 'amber' : 'slate'"
          :variant="muted ? 'solid' : 'faded'"
          xs
          class="!rounded-full"
          @click="toggleMute"
        />
        <NextButton
          v-tooltip.top="t('CONNECTIONS.call.hangup')"
          icon="i-ph-phone-bold"
          ruby
          xs
          class="!rounded-full rotate-[135deg]"
          @click="endCall(true)"
        />
      </div>
    </div>
  </transition>
</template>

<style scoped>
.astracall-pop-enter-active,
.astracall-pop-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.astracall-pop-enter-from,
.astracall-pop-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.96);
}
</style>
